<template>
    <component :is="componentTag" v-if="isVisible" :id="link.customId || id" class="cms-link" v-on="$listeners" :to="to"
        :href="href" :target="target" :role="unavailableItemLink ? 'button' : null"
        :tabindex="unavailableItemLink ? 0 : null" @click="onClick" @keydown="onKeydown">
        <template v-if="isDisplayingReview">
            <span class="review-debug f-9"><i class="fas fa-link" /> {{ link.displayName }}</span>
        </template>
        <slot name="before" />
        <slot></slot>
        <slot name="after" />
    </component>
</template>

<script>
// cms-link #hot-reload-debug
import CmsLink from "@/helpers/cms/CmsLink.js";
import SessionTracker from "@/helpers/SessionTracker.js";
import {createLinkClickAction} from "@/helpers/cms/CmsAnalyticsActionHelper.js";
import {isUnavailableCmsItemLink} from "@/helpers/cms/CmsItemNavigation.js";

// Clicks are recorded by one listener on the document, not by the listeners of each link: it doesn't depend
// on how the root element of a link is rendered (router-link, a, div...), and nothing between the link and
// the document can stop it. It looks for the innermost link around the clicked element that has something to
// record (ex: a button in a card that is a link), and only that link records the click.
const linksByElement = new WeakMap();
let isClickRecordingInstalled = false;

function recordClickOnInnermostLink(event) {
    for (let element = event.target; element; element = element.parentElement) {
        const link = linksByElement.get(element);
        if (!link) {
            continue;
        }

        // The analytics must never get in the way of a click, whatever a link does
        try {
            if (link.recordClick()) {
                return;
            }
        } catch (e) {
            console.error(`Could not record the click on the link`, e);
            return;
        }
    }
}

function installClickRecording() {
    if (!isClickRecordingInstalled) {
        isClickRecordingInstalled = true;
        document.addEventListener(`click`, recordClickOnInnermostLink, true);
    }
}

export default {
    name: `cms-link`,
    inject: {
        getBlockContext: {default: null}
    },
    props: {
        link: { type: [Object, String] },
        boundItem: { type: Object },
        id: { type: String },
        inMainMenu: { type: Boolean },
        navItem: { type: Boolean }
    },
    computed: {
        componentTag() {
            if (this.navItem) {
                return `b-nav-item`;
            } else if (this.to) {
                return `router-link`;
            } else if (this.href) {
                return `a`;
            } else {
                return `div`;
            }
        },
        // ensure that page link are always up-to-date
        cmsLink() {
            if (this.link) {
                return new CmsLink(this.link);
            } else {
                return this.link;
            }
        },
        linkedCmsItem() {
            return this.boundItem || (this.cmsLink?.type === `item` ? this.cmsLink.getItem() : null);
        },
        unavailableItemLink() {
            if (this.isEditMode || !this.cmsLink) {
                return false;
            }
            return isUnavailableCmsItemLink(this.linkedCmsItem, this.cmsLink.to || this.cmsLink.href, this.cmsWebsite);
        },
        to() {
            if (this.isEditMode || !this.cmsLink || this.unavailableItemLink) {
                return null;
            }

            let link = this.cmsLink.to;
            if (this.cmsLink.params) {
                if (link.indexOf(`?`) > 0) {
                    link += this.cmsLink.params;
                } else {
                    link += `?${this.cmsLink.params}`;
                }
            }
            return link;
        },
        href() {
            if (this.isEditMode || !this.cmsLink || this.unavailableItemLink) {
                return null;
            }

            if (this.to) {
                return `${window.location.origin}${this.to}`;
            }

            let link = this.cmsLink.href;
            if (this.cmsLink.params) {
                if (link.indexOf(`?`) > 0) {
                    link += this.cmsLink.params;
                } else {
                    link += `?${this.cmsLink.params}`;
                }
            }
            return link;
        },
        target() {
            if (this.to || !this.cmsLink) {
                return null;
            }
            return this.cmsLink.openInNewTab ? `_blank` : ``;
        },
        isVisible() {
            if (!this.cmsLink) {
                console.error(`Link missing for CMS link`, this);
                return true;
            }

            if (this.cmsLink.type === `collapse` && !this.inMainMenu) {
                return false;
            } else if (this.cmsLink.type === `wheelchair` && !this.isKioskDevice) {
                return false;
            } else if (this.cmsLink.type === `offline`) {
                if (this.isKioskDevice) {
                    return !window.navigator.onLine;
                } else {
                    return false;
                }
            }

            if (!this.cmsLink.mediaQuery) {
                return true;
            } else {
                return this.cmsLink.mediaProxy.visible;
            }
        },
    },
    watch: {
        // Leaving or entering the edit mode doesn't always re-render the link
        isEditMode() {
            this.registerElement();
        }
    },
    mounted() {
        this.registerElement();
    },
    updated() {
        this.registerElement();
    },
    beforeDestroy() {
        this.unregisterElement();
    },
    methods: {
        // The root element changes when the link is hidden then shown again, or when its tag changes.
        // Nothing is recorded in the editor: its links are not registered.
        registerElement() {
            const element = this.isEditMode ? null : this.$el;
            if (this.registeredElement === element) {
                return;
            }
            this.unregisterElement();
            if (element?.nodeType === Node.ELEMENT_NODE) {
                linksByElement.set(this.$el, this);
                this.registeredElement = this.$el;
                installClickRecording();
            }
        },
        unregisterElement() {
            if (this.registeredElement && linksByElement.get(this.registeredElement) === this) {
                linksByElement.delete(this.registeredElement);
            }
            this.registeredElement = null;
        },
        /**
         * Record a click on this link for the analytics.
         * @returns {boolean} false when this link has nothing to record, a link around it can then record the click
         */
        recordClick() {
            if (this.isEditMode || !this.cmsLink || !this.cmsPage) {
                return false;
            }

            // Links that lead nowhere (the empty default link of blocks) have no action
            const action = createLinkClickAction({
                cmsLink: this.cmsLink,
                destination: this.to || this.href,
                opensInNewTab: this.target === `_blank`,
                page: this.cmsPage,
                path: this.$route?.path,
                blockContext: this.getBlockContext?.(),
                boundItem: this.boundItem
            });
            if (!action) {
                return false;
            }

            SessionTracker.trackAction(action);
            return true;
        },
        onKeydown(event) {
            if (event.target.closest(`.cms-link`) !== this.$el) {
                return;
            }
            if (this.unavailableItemLink && (event.key === `Enter` || event.key === ` `)) {
                event.preventDefault();
                this.onClick(event);
            }
        },
        onClick(event) {
            if (this.isEditMode || !this.cmsLink)
                return;

            if (this.unavailableItemLink) {
                if (event && event.target.closest(`.cms-link`) !== this.$el) {
                    return;
                }
                this.$store.commit(`showCmsItemInfo`, this.linkedCmsItem.id);
                return;
            }

            switch (this.cmsLink.type) {
                case `back`:
                    window.history.go(-1);
                    break;
                case `next`:
                    window.history.go(1);
                    break;
                case `collapse`:
                    this.$emit(`collapse-nav-menu`);
                    break;
                case `offline`:
                    // Full navigation: a router push never lands before the synchronous reload
                    window.location.assign(`/`);
                    break;
                case `wheelchair`:
                    this.$store.commit(`setAccessibilityWheelchair`, !this.$store.state.accessibility.wheelchair);
                    break;
                case `feedback`:
                    this.$store.commit(`showFeedbackModal`, {action: `nav-link`});
                    break;
                case `language`:
                    this.$store.state.languageSelectModal = true;
                    break;
            }
        }
    }
}
</script>

<style lang="scss" scoped>
</style>
